import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useBusiness } from './BusinessContext';
import { supabase } from '../lib/supabase';
import { createStripeCheckoutSession, handleStripeCheckoutSuccess } from '../lib/stripeCheckout.ts';
import { isStripeConfigured } from '../lib/stripe.ts';
import { getSiteUrl } from '../lib/siteConfig';

export interface StripeSubscriptionDetails {
  id: string;
  status: string;
  planName: string;
  planId: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  renewalDate: string;
  amount: number;
  currency: string;
  interval: string;
  customerEmail: string;
}

export interface BusinessSubscription {
  id: string;
  business_id: string;
  subscription_status: 'active' | 'inactive' | 'trialing' | 'past_due' | 'canceled';
  subscription_last_checked: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
  active_plan_id?: string; // Track which plan is active
  stripeDetails?: StripeSubscriptionDetails | null; // Stripe subscription details
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'monthly' | 'yearly';
  features: string[];
}

// Mock subscription plans (will be replaced with Stripe later)
const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'surge',
    name: 'Surge Plan',
    price: 129.99,
    interval: 'monthly',
    features: [
      'Everything in Core',
      'Advanced team management',
      'Advanced reporting & analytics',
      'Automations & workflows',
      'API access',
      'Priority support',
      'Custom integrations'
    ]
  }
];

interface SubscriptionContextType {
  subscription: BusinessSubscription | null;
  isActive: boolean;
  isLoading: boolean;
  error: string | null;
  plans: SubscriptionPlan[];
  refreshSubscription: () => Promise<void>;
  activateSubscription: (planId: string) => Promise<boolean>;
  deactivateSubscription: () => Promise<boolean>;
  syncResourceSubscription: () => Promise<boolean>;
  fetchStripeDetails: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { selectedBusiness } = useBusiness();
  const [subscription, setSubscription] = useState<BusinessSubscription | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans] = useState<SubscriptionPlan[]>(SUBSCRIPTION_PLANS);

  const fetchStripeDetails = async () => {
    if (!selectedBusiness || !subscription?.stripe_subscription_id) {
      return;
    }

    try {
      console.log('Fetching Stripe subscription details...');
      
      if (!supabase) {
        console.error('Supabase client not available');
        return;
      }

      const { data, error } = await supabase.functions.invoke('get-stripe-subscription', {
        body: { businessId: selectedBusiness.id }
      });

      if (error) {
        console.error('Error fetching Stripe details:', error);
        return;
      }

      if (data?.subscription) {
        console.log('Stripe details received:', data.subscription);
        setSubscription(prev => prev ? {
          ...prev,
          stripeDetails: data.subscription
        } : null);
      } else {
        console.log('No Stripe subscription details found');
        setSubscription(prev => prev ? {
          ...prev,
          stripeDetails: null
        } : null);
      }
    } catch (err) {
      console.error('Error fetching Stripe details:', err);
    }
  };

  const loadSubscription = async () => {
    if (!selectedBusiness) {
      setSubscription(null);
      setIsActive(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch subscription data from database
      if (!supabase) {
        setError('Database connection not available');
        return;
      }
      
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          id,
          subscription_status,
          subscription_last_checked,
          stripe_customer_id,
          stripe_subscription_id,
          active_plan_id,
          trial_enabled,
          trial_start_date,
          trial_end_date,
          trial_activated_by,
          trial_activated_at,
          created_at,
          updated_at
        `)
        .eq('id', selectedBusiness.id)
        .single();

      if (error) {
        console.error('Error fetching subscription:', error);
        setError('Failed to load subscription status');
        return;
      }

      if (data) {
        const subscriptionData: BusinessSubscription = {
          id: data.id,
          business_id: data.id,
          subscription_status: data.subscription_status || 'inactive',
          subscription_last_checked: data.subscription_last_checked,
          stripe_customer_id: data.stripe_customer_id,
          stripe_subscription_id: data.stripe_subscription_id,
          created_at: data.created_at,
          updated_at: data.updated_at,
          active_plan_id: data.active_plan_id
        };

        setSubscription(subscriptionData);
        
        // Check if subscription is active (including trials)
        const isActiveSubscription = data.subscription_status === 'active' || 
          (data.trial_enabled && 
           data.trial_end_date && 
           new Date(data.trial_end_date) > new Date());
        
        setIsActive(isActiveSubscription);

        // If we have a Stripe subscription ID, fetch the details
        if (data.stripe_subscription_id) {
          await fetchStripeDetails();
        }
      } else {
        // No subscription data found, create default inactive subscription
        const defaultSubscription: BusinessSubscription = {
          id: selectedBusiness.id,
          business_id: selectedBusiness.id,
          subscription_status: 'inactive',
          subscription_last_checked: null,
          stripe_customer_id: null,
          stripe_subscription_id: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        setSubscription(defaultSubscription);
        setIsActive(false);
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError('Failed to load subscription status');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSubscription = async () => {
    await loadSubscription();
  };

  const activateSubscription = async (planId: string): Promise<boolean> => {
    if (!selectedBusiness) return false;

    try {
      setIsLoading(true);
      setError(null);

      // Check if Stripe is configured
      if (!isStripeConfigured()) {
        setError('Stripe is not configured. Please contact support.');
        return false;
      }

      // Find the plan details
      const selectedPlan = SUBSCRIPTION_PLANS.find(plan => plan.id === planId);
      console.log(`Initiating Stripe checkout for business ${selectedBusiness.id} with plan ${planId}`);
      console.log(`Selected plan: ${selectedPlan?.name} - $${selectedPlan?.price}/month`);

      // Get user email from Supabase auth
      if (!supabase) {
        setError('Database connection not available');
        return false;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        setError('User email not found');
        return false;
      }

      // Create Stripe checkout session
      await createStripeCheckoutSession({
        planId,
        businessId: selectedBusiness.id,
        customerEmail: user.email,
        successUrl: `${getSiteUrl()}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${getSiteUrl()}/admin-subscription-management`
      });

      return true;
    } catch (err) {
      console.error('Error creating Stripe checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to create checkout session');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

    const deactivateSubscription = async (): Promise<boolean> => {
    if (!selectedBusiness) return false;

    try {
      setIsLoading(true);
      setError(null);

      if (!supabase) {
        setError('Database connection not available');
        return false;
      }

      console.log(`Deactivating subscription for business ${selectedBusiness.id}`);
      
      // Update the business record in the database
      const { error: businessError } = await supabase
        .from('businesses')
        .update({
          subscription_status: 'inactive',
          subscription_last_checked: new Date().toISOString(),
          active_plan_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedBusiness.id);

      if (businessError) {
        console.error('Error updating business subscription in database:', businessError);
        setError('Failed to deactivate subscription');
        return false;
      }

      // Update all resources for this business to have inactive subscription status
      const { error: resourcesError } = await supabase
        .from('resources')
        .update({
          subscription_status: 'inactive',
          updated_at: new Date().toISOString()
        })
        .eq('business_id', selectedBusiness.id);

      if (resourcesError) {
        console.error('Error updating resources subscription status:', resourcesError);
        // Don't fail the entire operation, just log the error
        console.warn('Business subscription deactivated but some resources may not be updated');
      } else {
        console.log(`✅ Updated subscription status for all resources in business ${selectedBusiness.id}`);
      }

      // Update local state
      const updatedSubscription: BusinessSubscription = {
        id: selectedBusiness.id,
        business_id: selectedBusiness.id,
        subscription_status: 'inactive',
        subscription_last_checked: new Date().toISOString(),
        stripe_customer_id: null,
        stripe_subscription_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      setSubscription(updatedSubscription);
      setIsActive(false);

      console.log(`✅ Successfully deactivated subscription`);
      return true;
    } catch (err) {
      console.error('Error deactivating subscription:', err);
      setError('Failed to deactivate subscription');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const syncResourceSubscription = async (): Promise<boolean> => {
    if (!selectedBusiness) return false;

    try {
      setIsLoading(true);
      setError(null);

      if (!supabase) {
        setError('Database connection not available');
        return false;
      }

      console.log(`Syncing resource subscription status for business ${selectedBusiness.id}`);
      
      // Update all resources for this business to match the business subscription status
      const { error } = await supabase
        .from('resources')
        .update({
          subscription_status: subscription?.subscription_status || 'inactive',
          updated_at: new Date().toISOString()
        })
        .eq('business_id', selectedBusiness.id);

      if (error) {
        console.error('Error syncing resource subscription status:', error);
        setError('Failed to sync resource subscription status');
        return false;
      }

      console.log(`✅ Successfully synced resource subscription status for business ${selectedBusiness.id}`);
      return true;
    } catch (err) {
      console.error('Error syncing resource subscription status:', err);
      setError('Failed to sync resource subscription status');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubscription();
  }, [selectedBusiness]);

  const value: SubscriptionContextType = {
    subscription,
    isActive,
    isLoading,
    error,
    plans,
    refreshSubscription,
    activateSubscription,
    deactivateSubscription,
    syncResourceSubscription,
    fetchStripeDetails,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 